/**
 * El formulario de una competencia: alta y edición de la ficha completa.
 *
 * Es la pantalla que cierra el ítem 1.2 del plan de 90 días: hasta acá el
 * portal mostraba fixture, tabla y ficha, pero **crear** la competencia —fases,
 * grupos, sistema de puntos y desempates— seguía siendo `curl` contra
 * `PUT /api/competitions/:id`. Si en la reunión piden *"cargámelo en vivo"*,
 * esto es lo que evita abrir la consola.
 *
 * Todo el parseo y la validación viven en `lib/competition-form.ts` (puros y
 * testeados sin DOM); acá sólo hay estado de formulario y presentación.
 *
 * Se usa desde dos lados —el listado (`CompetitionsPage`) y la ficha
 * (`CompetitionPage` → pestaña Ficha)— porque son los dos lugares donde a uno
 * le agarran ganas de editar, y duplicar el formulario sería garantizar que
 * uno de los dos quede viejo.
 */
import { useState } from 'react';
import type { Competition, CompetitionStatus, PhaseKind, TiebreakCriterion } from '@scout/core';
import type { MessageKey, TFunction } from '../../i18n/index';
import { useT } from '../../i18n/use-t';
import { Card } from '../../components/States';
import { errorMessage } from '../../hooks';
import type { ApiClient } from '../../lib/api-client';
import { competitionStatusLabelKey, tiebreakLabelKey } from '../../lib/competition';
import {
  PHASE_KINDS,
  SCORING_PRESET_IDS,
  STATUSES,
  addTiebreaker,
  applyScoringPreset,
  availableTiebreakers,
  buildCompetitionPayload,
  competitionToForm,
  emptyCompetitionForm,
  emptyPhase,
  moveTiebreaker,
  parseTeamsText,
  removeTiebreaker,
  scoringPresetLabelKey,
  slugifyId,
  sportConfigOptions,
  DEFAULT_TIEBREAKERS,
  type CompetitionFormValues,
  type PhaseFormValues,
  type ScoringFormValues,
  type ScoringPresetId,
} from '../../lib/competition-form';

const PHASE_KIND_KEYS: Record<PhaseKind, MessageKey> = {
  regular: 'competition.phaseKindRegular',
  groups: 'competition.phaseKindGroups',
  playoff: 'competition.phaseKindPlayoff',
};

export function CompetitionForm({
  client,
  initial,
  onSaved,
  onCancel,
}: {
  client: ApiClient;
  /** `null` = alta. Con ficha, el id queda bloqueado. */
  initial: Competition | null;
  onSaved: (competition: Competition) => void;
  onCancel: () => void;
}): JSX.Element {
  const t = useT();
  const [values, setValues] = useState<CompetitionFormValues>(() =>
    initial === null ? emptyCompetitionForm(t) : competitionToForm(initial),
  );
  const [errors, setErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  /** Al crear, el id se propone desde el nombre hasta que alguien lo toca. */
  const [idTouched, setIdTouched] = useState(initial !== null);

  const creando = initial === null;
  const patch = (cambio: Partial<CompetitionFormValues>): void =>
    setValues((prev) => ({ ...prev, ...cambio }));

  /**
   * Tocar cualquier número del sistema de puntos lo saca del preset: si
   * siguiera diciendo "FIVB" después de editarlo a mano, el selector estaría
   * mintiendo sobre lo que se va a guardar.
   */
  const patchScoring = (cambio: Partial<ScoringFormValues>): void =>
    patch({ scoring: { ...values.scoring, ...cambio, presetId: 'custom' } });

  const setName = (name: string): void => {
    const propuesto = idTouched ? values.competitionId : slugifyId(name);
    patch({ name, competitionId: propuesto });
  };

  const patchPhase = (index: number, cambio: Partial<PhaseFormValues>): void =>
    patch({
      phases: values.phases.map((phase, i) => (i === index ? { ...phase, ...cambio } : phase)),
    });

  const setPhaseName = (index: number, name: string): void => {
    const phase = values.phases[index];
    if (!phase) return;
    // Mismo criterio que el id de la competencia, pero sin estado extra: si el
    // id todavía es el que se propuso solo, sigue el nombre; si lo escribieron
    // a mano (o vino del server), no se toca.
    const auto = phase.id === '' || phase.id === slugifyId(phase.name);
    patchPhase(index, { name, ...(auto && !phase.existing ? { id: slugifyId(name) } : {}) });
  };

  const guardar = async (): Promise<void> => {
    const payload = buildCompetitionPayload(values, t);
    if (!payload.ok) {
      setErrors(payload.errors);
      return;
    }
    setErrors([]);
    setSaving(true);
    try {
      const saved = await client.saveCompetition(payload.competitionId, payload.input);
      onSaved(saved);
    } catch (err) {
      setErrors([errorMessage(err instanceof Error ? err : new Error(String(err)))]);
    } finally {
      setSaving(false);
    }
  };

  const equipos = parseTeamsText(values.teamsText, t);
  const disponibles = availableTiebreakers(values.tiebreakers);

  return (
    <Card title={creando ? t('competitionForm.newCompetition') : t('competitionForm.editing', { id: values.competitionId })}>
      {errors.length > 0 ? (
        <div className="error-box" role="alert">
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {errors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* --- Identidad ------------------------------------------------------ */}
      <h3>{t('competitionForm.identityTitle')}</h3>
      <div className="grid-2">
        <label className="field">
          <span>{t('competitionForm.name')}</span>
          <input
            value={values.name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('competitionForm.namePlaceholder')}
          />
        </label>
        <label className="field">
          <span>{t('competitionForm.competitionId')}</span>
          <input
            value={values.competitionId}
            disabled={!creando}
            onChange={(e) => {
              setIdTouched(true);
              patch({ competitionId: e.target.value });
            }}
            placeholder={t('competitionForm.competitionIdPlaceholder')}
          />
        </label>
        <label className="field">
          <span>{t('competitionForm.season')}</span>
          <input
            value={values.season}
            onChange={(e) => patch({ season: e.target.value })}
            placeholder={t('competitionForm.seasonPlaceholder')}
          />
        </label>
        <label className="field">
          <span>{t('competitionForm.status')}</span>
          <select
            value={values.status}
            onChange={(e) => patch({ status: e.target.value as CompetitionStatus })}
          >
            {STATUSES.map((status) => (
              <option key={status} value={status}>
                {t(competitionStatusLabelKey(status))}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{t('competitionForm.sportConfig')}</span>
          <select
            value={values.sportConfigId}
            onChange={(e) => patch({ sportConfigId: e.target.value })}
          >
            {sportConfigOptions(values.sportConfigId).map((id) => (
              <option key={id} value={id}>
                {id}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="hint">{t('competitionForm.competitionIdHint')}</p>

      <label className="field">
        <span>{t('competitionForm.description')}</span>
        <textarea
          rows={2}
          value={values.description}
          onChange={(e) => patch({ description: e.target.value })}
        />
      </label>

      {/* --- Equipos -------------------------------------------------------- */}
      <h3>{t('competitionForm.teamsTitle')}</h3>
      <p className="hint">{t('competitionForm.teamsHint')}</p>
      <label className="field">
        <span>{t('competitionForm.teamsCount', { count: equipos.teams.length })}</span>
        <textarea
          rows={8}
          value={values.teamsText}
          onChange={(e) => patch({ teamsText: e.target.value })}
          placeholder={t('competitionForm.teamsPlaceholder')}
        />
      </label>

      {/* --- Sistema de puntos ---------------------------------------------- */}
      <h3>{t('competitionForm.scoringTitle')}</h3>
      <p className="hint">{t('competitionForm.scoringHint')}</p>
      <div className="grid-2">
        <label className="field">
          <span>{t('competitionForm.preset')}</span>
          <select
            value={values.scoring.presetId}
            onChange={(e) => {
              const id = e.target.value;
              if (id === 'custom') {
                patch({ scoring: { ...values.scoring, presetId: 'custom' } });
                return;
              }
              patch({ scoring: applyScoringPreset(id as ScoringPresetId, t) });
            }}
          >
            {SCORING_PRESET_IDS.map((id) => (
              <option key={id} value={id}>
                {t(scoringPresetLabelKey(id))}
              </option>
            ))}
            <option value="custom">{t('competitionForm.presetCustom')}</option>
          </select>
        </label>
        <label className="field">
          <span>{t('competitionForm.scoringLabel')}</span>
          <input
            value={values.scoring.label}
            onChange={(e) => patch({ scoring: { ...values.scoring, label: e.target.value } })}
          />
        </label>
      </div>

      <label className="field">
        <span>{t('competitionForm.rules')}</span>
        <textarea
          rows={4}
          value={values.scoring.rulesText}
          onChange={(e) => patchScoring({ rulesText: e.target.value })}
          placeholder={t('competitionForm.rulesPlaceholder')}
        />
      </label>
      <p className="hint">{t('competitionForm.rulesHint')}</p>

      <div className="grid-2">
        <label className="field">
          <span>{t('competitionForm.winPoints')}</span>
          <input
            value={values.scoring.winPoints}
            onChange={(e) => patchScoring({ winPoints: e.target.value })}
            inputMode="decimal"
          />
        </label>
        <label className="field">
          <span>{t('competitionForm.lossPoints')}</span>
          <input
            value={values.scoring.lossPoints}
            onChange={(e) => patchScoring({ lossPoints: e.target.value })}
            inputMode="decimal"
          />
        </label>
      </div>
      <p className="hint">{t('competitionForm.fallbackHint')}</p>

      <h4>{t('competitionForm.forfeitTitle')}</h4>
      <div className="grid-2">
        <label className="field">
          <span>{t('competitionForm.forfeitSets')}</span>
          <input
            value={values.scoring.forfeitWinnerSets}
            onChange={(e) => patchScoring({ forfeitWinnerSets: e.target.value })}
            inputMode="numeric"
          />
        </label>
        <label className="field">
          <span>{t('competitionForm.forfeitLoserSets')}</span>
          <input
            value={values.scoring.forfeitLoserSets}
            onChange={(e) => patchScoring({ forfeitLoserSets: e.target.value })}
            inputMode="numeric"
          />
        </label>
        <label className="field">
          <span>{t('competitionForm.forfeitPointsPerSet')}</span>
          <input
            value={values.scoring.forfeitPointsPerSet}
            onChange={(e) => patchScoring({ forfeitPointsPerSet: e.target.value })}
            inputMode="numeric"
          />
        </label>
        <label className="field">
          <span>{t('competitionForm.forfeitLoserPoints')}</span>
          <input
            value={values.scoring.forfeitLoserPoints}
            onChange={(e) => patchScoring({ forfeitLoserPoints: e.target.value })}
            inputMode="decimal"
          />
        </label>
        <label className="field">
          <span>{t('competitionForm.forfeitWinnerPoints')}</span>
          <input
            value={values.scoring.forfeitWinnerPoints}
            onChange={(e) => patchScoring({ forfeitWinnerPoints: e.target.value })}
            inputMode="decimal"
            placeholder={t('competitionForm.forfeitWinnerPointsHint')}
          />
        </label>
      </div>

      {/* --- Desempates ----------------------------------------------------- */}
      <h3>{t('competitionForm.tiebreakTitle')}</h3>
      <p className="hint">{t('competitionForm.tiebreakHint')}</p>
      {values.tiebreakers.length === 0 ? (
        <p className="hint">{t('competitionForm.tiebreakEmptyHint')}</p>
      ) : (
        <ol>
          {values.tiebreakers.map((criterion, index) => (
            <li key={criterion} style={{ marginBottom: 6 }}>
              <span className="row" style={{ gap: 6 }}>
                <span>{t(tiebreakLabelKey(criterion))}</span>
                <button
                  type="button"
                  className="small"
                  disabled={index === 0}
                  title={t('competitionForm.tiebreakUp')}
                  onClick={() => patch({ tiebreakers: moveTiebreaker(values.tiebreakers, index, -1) })}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="small"
                  disabled={index === values.tiebreakers.length - 1}
                  title={t('competitionForm.tiebreakDown')}
                  onClick={() => patch({ tiebreakers: moveTiebreaker(values.tiebreakers, index, 1) })}
                >
                  ↓
                </button>
                <button
                  type="button"
                  className="small danger"
                  onClick={() =>
                    patch({ tiebreakers: removeTiebreaker(values.tiebreakers, criterion) })
                  }
                >
                  {t('competitionForm.tiebreakRemove')}
                </button>
              </span>
            </li>
          ))}
        </ol>
      )}
      <div className="row">
        {disponibles.length > 0 ? (
          <select
            value=""
            onChange={(e) => {
              if (e.target.value === '') return;
              patch({
                tiebreakers: addTiebreaker(values.tiebreakers, e.target.value as TiebreakCriterion),
              });
            }}
          >
            <option value="">{t('competitionForm.tiebreakAdd')}</option>
            {disponibles.map((criterion) => (
              <option key={criterion} value={criterion}>
                {t(tiebreakLabelKey(criterion))}
              </option>
            ))}
          </select>
        ) : null}
        <button
          type="button"
          className="small"
          onClick={() => patch({ tiebreakers: [...DEFAULT_TIEBREAKERS] })}
        >
          {t('competitionForm.tiebreakRestore')}
        </button>
      </div>

      {/* --- Fases ---------------------------------------------------------- */}
      <h3>{t('competitionForm.phasesTitle')}</h3>
      <p className="hint">{t('competitionForm.phasesHint')}</p>
      {values.phases.length === 0 ? <p className="empty">{t('competitionForm.phasesEmpty')}</p> : null}

      {values.phases.map((phase, index) => {
        const preservado = preservedDetail(phase, t);
        return (
        <fieldset
          key={phase.existing ? phase.id : `nueva-${index}`}
          style={{ border: '1px solid var(--line)', borderRadius: 6, padding: 12, marginBottom: 12 }}
        >
          <legend className="hint">{t('competitionForm.phaseNumber', { number: index + 1 })}</legend>
          <div className="grid-2">
            <label className="field">
              <span>{t('competitionForm.phaseName')}</span>
              <input
                value={phase.name}
                onChange={(e) => setPhaseName(index, e.target.value)}
                placeholder={t('competitionForm.phaseNamePlaceholder')}
              />
            </label>
            <label className="field">
              <span>{t('competitionForm.phaseId')}</span>
              <input
                value={phase.id}
                disabled={phase.existing}
                onChange={(e) => patchPhase(index, { id: e.target.value })}
              />
            </label>
            <label className="field">
              <span>{t('competitionForm.phaseKind')}</span>
              <select
                value={phase.kind}
                onChange={(e) => patchPhase(index, { kind: e.target.value as PhaseKind })}
              >
                {PHASE_KINDS.map((kind) => (
                  <option key={kind} value={kind}>
                    {t(PHASE_KIND_KEYS[kind])}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>{t('competitionForm.phaseLegs')}</span>
              <select
                value={String(phase.legs)}
                onChange={(e) => patchPhase(index, { legs: e.target.value === '2' ? 2 : 1 })}
              >
                <option value="1">{t('competition.legsOne')}</option>
                <option value="2">{t('competition.legsTwo')}</option>
              </select>
            </label>
          </div>

          <label className="field">
            <span>{t('competitionForm.phaseTeams')}</span>
            <input
              value={phase.teamsText}
              onChange={(e) => patchPhase(index, { teamsText: e.target.value })}
              placeholder="bolivar, upcn"
            />
          </label>
          <p className="hint">{t('competitionForm.phaseTeamsHint')}</p>

          {phase.kind === 'groups' ? (
            <>
              <label className="field">
                <span>{t('competitionForm.phaseGroups')}</span>
                {/* El ejemplo del formato vive en el hint traducido, no en un
                    placeholder en castellano dentro del JSX. */}
                <textarea
                  rows={3}
                  value={phase.groupsText}
                  onChange={(e) => patchPhase(index, { groupsText: e.target.value })}
                />
              </label>
              <p className="hint">{t('competitionForm.phaseGroupsHint')}</p>
            </>
          ) : null}

          {preservado !== null ? (
            <p className="hint">{t('competitionForm.phasePreserved', { detail: preservado })}</p>
          ) : null}

          <button
            type="button"
            className="small danger"
            onClick={() => patch({ phases: values.phases.filter((_, i) => i !== index) })}
          >
            {t('competitionForm.removePhase')}
          </button>
        </fieldset>
        );
      })}

      <div className="row">
        <button
          type="button"
          className="small"
          onClick={() => patch({ phases: [...values.phases, emptyPhase()] })}
        >
          {t('competitionForm.addPhase')}
        </button>
      </div>

      {/* --- Acciones ------------------------------------------------------- */}
      <div className="row" style={{ marginTop: 16 }}>
        <button type="button" className="primary" disabled={saving} onClick={() => void guardar()}>
          {saving ? t('common.saving') : t('common.save')}
        </button>
        <button type="button" disabled={saving} onClick={onCancel}>
          {t('common.cancel')}
        </button>
      </div>
    </Card>
  );
}

/**
 * Qué trae esta fase que el formulario transporta pero no edita. Se dice en
 * pantalla en vez de callarlo: quien abre la ficha tiene que saber que su
 * quita de puntos por sanción sigue ahí después de guardar.
 */
function preservedDetail(phase: PhaseFormValues, t: TFunction): string | null {
  const partes: string[] = [];
  if (phase.preserved.scoring !== undefined) partes.push(t('competitionForm.phasePreservedScoring'));
  if (phase.preserved.tiebreakers !== undefined) {
    partes.push(t('competitionForm.phasePreservedTiebreakers'));
  }
  if (phase.preserved.adjustments.length > 0) {
    partes.push(
      t('competitionForm.phasePreservedAdjustments', { count: phase.preserved.adjustments.length }),
    );
  }
  return partes.length === 0 ? null : partes.join(' · ');
}
